import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FieldError, FieldErrors, UseFormRegister } from 'react-hook-form';

import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import calendarIcon from '@/assets/icons/calender.svg';

type FormFieldError = FieldError | undefined | FieldErrors<any>;

interface DatePickerFieldProps {
  name: string;
  label?: string;
  description?: string;
  placeholder?: string;
  value?: Date | null;
  onChange?: (date: Date | null) => void;
  disabled?: boolean;
  register?: UseFormRegister<any>;
  error?: FormFieldError;
  className?: string;
  size?: string;
  tooltipText?: string;
  minDate?: Date;
  maxDate?: Date;
}

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  name,
  label,
  description,
  placeholder = 'Select date',
  value,
  onChange,
  disabled = false,
  register,
  error,
  className,
  size = '',
  tooltipText,
  minDate,
  maxDate
}) => {
  // get error message
  const errorMessage = error && 'message' in error ? error.message : '';

  const CustomInput = React.forwardRef<HTMLDivElement, { value?: string; onClick?: () => void }>(
    ({ value, onClick }, ref) => (
      <div
        className={`${size === 'sm' ? 'h-[42px]' : 'h-[44px] md:h-[64.88px]'} bg-lightGray rounded-[20px] border w-full font-medium text-base p-[0.585rem] ${size === 'sm' ? 'md:p-[0.45rem] md:px-[0.875rem]' : 'md:p-[1.215rem]'} px-4 pr-3
          flex items-center justify-between min-w-0
          ${disabled ? 'opacity-50 cursor-not-allowed hover:border-transparent' : 'cursor-pointer '}
          ${error ? 'border-red-500' : 'hover:border-[#ADB5BD]'}`}
        onClick={onClick}
        ref={ref}
      >
        <span
          className={`text-[13px] sm:text-base min-w-0 overflow-hidden text-ellipsis whitespace-nowrap ${!value ? 'text-[#93999F]' : 'text-[#495057]'}`}
        >
          {value || placeholder}
        </span>
        <img src={calendarIcon} alt="calendar-icon" className="flex-shrink-0" />
      </div>
    )
  );

  CustomInput.displayName = 'CustomInput';

  const handleDateChange = (date: Date | null) => {
    if (register && date) {
      const { onChange: registeredOnChange } = register(name);
      registeredOnChange({ target: { value: date } });
    }
    if (onChange) {
      onChange(date);
    }
  };

  return (
    <div className={`${className ? className : ''}`}>
      {label && (
        <div className={`flex items-center gap-2 ${size === 'sm' ? 'mb-2 md:mb-[0.45rem]' : 'mb-2'}`}>
          <label className={`text-[#495057] font-medium text-xs ${size === 'sm' ? 'md:text-sm' : 'md:text-lg'} block`}>
            {label}
          </label>
          {tooltipText && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="text-primary font-bold text-xs">
                    ?
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{tooltipText}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      )}
      <ReactDatePicker
        selected={value}
        onChange={handleDateChange}
        disabled={disabled}
        customInput={<CustomInput />}
        wrapperClassName="w-full"
        dateFormat="MMM dd, yyyy"
        calendarClassName="rounded-lg shadow-lg border border-gray-200"
        minDate={minDate}
        maxDate={maxDate}
      />
      {(errorMessage || description) && (
        <p className={`mt-2 text-xs ${errorMessage ? 'text-red-600' : 'text-[#93999F]'}`}>
          {(errorMessage as String) || description}
        </p>
      )}
    </div>
  );
};

export default DatePickerField;
